import { AtendimentoProfissional, Medicao, Problema, TipoAcompanhamentoEnum } from 'graphql/types.generated'
import { calcularIMC } from 'util/atendimento/calculateImc'
import { toDate } from 'util/date/formatDate'
import { isEmpty } from 'util/validation/Util'

import { GANHO_PESO_VALORES_EIXO_X } from '../../components/modals/medicoes/graficos/pre-natal/ganho-peso/converter-graficoGanhoPeso'
import { categoriaImcReferenciaRecord } from '../../components/modals/medicoes/graficos/pre-natal/ganho-peso/model-graficoGanhoPeso'
import { getCategoriaImcReferencia } from '../../components/modals/medicoes/graficos/pre-natal/ganho-peso/util-graficoGanhoPeso'
import { MedicaoAntropometricaPreNatal } from '../../components/modals/medicoes/pre-natal/model-medicoesPreNatal'
import { MedicoesPreNatal } from '../../components/modals/types/model-historicoPreNatal'
import { convertMedicoesUltimaGestacao } from '../../soap/aside/pre-natal/converter-acompanhamentoPreNatal'
import { AcompanhamentoPreNatalQueryModel } from '../../soap/aside/pre-natal/model-acompanhamentoPreNatal'
import { ciapsAndCidsToSortProblemasRecord } from '../model-acompanhamentos'
import {
  MEDICOES_ACOMPANHAMENTO_PRE_NATAL,
  MedicoesAcompanhamentoPreNatal,
  PERCENTIS_POR_CATEGORIA,
} from './model-acompanhamentoPreNatal'

export const sortProblemas = (a: Problema, b: Problema, tipoAcompanhamento: TipoAcompanhamentoEnum) => {
  const codigosToSort = ciapsAndCidsToSortProblemasRecord[tipoAcompanhamento]

  const aIsInAcompanhamento = codigosToSort.ciaps.has(a.ciap?.codigo) || codigosToSort.cids.has(a.cid10?.codigo)
  const bIsInAcompanhamento = codigosToSort.ciaps.has(b.ciap?.codigo) || codigosToSort.cids.has(b.cid10?.codigo)

  if (aIsInAcompanhamento && !bIsInAcompanhamento) {
    return -1
  }

  if (!aIsInAcompanhamento && bIsInAcompanhamento) {
    return 1
  }

  return a.situacao.localeCompare(b.situacao)
}

export const getMedicoesPreNatal = (
  atendimentosProfUltimaGestacao: (AtendimentoProfissional | AcompanhamentoPreNatalQueryModel)[]
) => {
  return atendimentosProfUltimaGestacao?.map((atendProf, index) => {
    const medicao = !isEmpty(atendProf.medicoes) ? atendProf.medicoes[0] : null
    const dataMedicao = medicao?.dataMedicao ?? atendProf.iniciadoEm
    return convertMedicoesUltimaGestacao(index, medicao, atendProf.atendimentoProfissionalPreNatal, dataMedicao)
  })
}

export const convertToUltimasMedicoesRegistradas = (
  medicoesPreNatal: MedicoesPreNatal[],
  outrasMedicoes?: Medicao[]
): MedicoesAcompanhamentoPreNatal => {
  const ultimasMedicoesPreNatal: MedicoesAcompanhamentoPreNatal = {}

  const allMedicoes: MedicoesPreNatal[] = medicoesPreNatal?.concat(
    outrasMedicoes?.map(
      (medicao: Medicao): MedicoesPreNatal => ({
        dataDaMedicao: medicao.dataMedicao,
        peso: medicao.valorPeso,
        pressaoArterial: medicao.valorPressaoArterial,
        glicemiaCapilar: medicao.valorGlicemia,
        isRegistradoAgora: false,
      })
    ) ?? []
  )

  const ordenadas = allMedicoes?.toSorted((a, b) => {
    const dateDiff = toDate(b.dataDaMedicao).getTime() - toDate(a.dataDaMedicao).getTime()
    if (dateDiff !== 0) return dateDiff
    return (b.numeroConsulta ?? 0) - (a.numeroConsulta ?? 0)
  })

  MEDICOES_ACOMPANHAMENTO_PRE_NATAL.forEach((key) => {
    const campo = key as keyof MedicoesPreNatal
    const registroMaisRecente = ordenadas?.find((valor) => !isEmpty(valor[campo]))

    if (registroMaisRecente) {
      ultimasMedicoesPreNatal[campo] = {
        medicao: registroMaisRecente[campo],
        dataMedicao: registroMaisRecente.dataDaMedicao,
      }
    }
  })

  return ultimasMedicoesPreNatal
}

export const calculatePercentilPesoGestacional = (
  semanaAlvo: number,
  ganhoPeso: number,
  medicaoReferencia: MedicaoAntropometricaPreNatal
): string => {
  const imcReferencia = calcularIMC(medicaoReferencia?.peso, medicaoReferencia?.altura)
  const indiceSemana = GANHO_PESO_VALORES_EIXO_X.indexOf(semanaAlvo)

  const categoria = getCategoriaImcReferencia(imcReferencia)
  const labels = PERCENTIS_POR_CATEGORIA[categoria]

  const areasReferencia = categoriaImcReferenciaRecord[categoria].referenceAreas
  const linhasPercentis = areasReferencia.slice(1)

  const limitesSuperioresNaSemana = linhasPercentis.map((referencia) => referencia.area[indiceSemana]?.upperLimit)
  const indiceLinhaSuperior = limitesSuperioresNaSemana.findIndex((limiteY) => ganhoPeso <= (limiteY as number))

  if (indiceLinhaSuperior === -1) return labels.last()

  return labels[indiceLinhaSuperior]
}

export const removeVacinaPrefix = (name: string): string => {
  return name.replace(/^vacina\s+/i, '').trim()
}
